import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import Navbar from '../components/Navbar';
import DashboardPage from './DashboardPage';
import CustomersPage from './CustomersPage';
import CustomerOnboardingPage from './CustomerOnboardingPage';
import FeedbackFormPage from './FeedbackFormPage';
import OrdersPage from './OrdersPage';
import OrderDetailPage from './OrderDetailPage';
import RepairsPage from './RepairsPage';
import SubscriptionPage from './SubscriptionPage';
import GoldSalePage from './GoldSalePage';
import OldGoldPage from './OldGoldPage';
import ProfilePage from './ProfilePage';
import SegmentDetailPage from './SegmentDetailPage';
import { FaExclamationTriangle, FaBars, FaTimes, FaGem, FaLock } from 'react-icons/fa';
import WelcomeModal from '../components/WelcomeModal';

export const MainApp: React.FC = () => {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [subParams, setSubParams] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showWelcome, setShowWelcome] = useState(() => {
    try {
      return !localStorage.getItem('apnicrm_welcome_seen');
    } catch {
      return false;
    }
  });
  const { isAuthenticated, subscription } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to='/login' />;
  }

  // ── Plan gate: expired/cancelled accounts must renew before using the app ──
  const planLocked =
    !!subscription &&
    (subscription.status === 'EXPIRED' || subscription.status === 'CANCELLED' ||
      (subscription.status === 'TRIAL' &&
        subscription.trialEnd &&
        new Date(subscription.trialEnd) < new Date()));

  const closeWelcome = () => {
    try {
      localStorage.setItem('apnicrm_welcome_seen', '1');
    } catch {}
    setShowWelcome(false);
  };

  const navigateTo = (page: string, id?: string) => {
    if (page.includes(':')) {
      const [base, param] = page.split(':');
      setCurrentPage(base);
      setSubParams(param);
    } else {
      setCurrentPage(page);
      // id is the record id (e.g. orderId) passed by list views
      setSubParams(id ?? null);
    }
    setMobileMenuOpen(false);
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <DashboardPage onNavigate={navigateTo} />;
      case 'customers':
        return <CustomersPage onNavigate={navigateTo} />;
      case 'onboarding':
        return <CustomerOnboardingPage onNavigate={navigateTo} />;
      case 'feedback':
        return <FeedbackFormPage />;
      case 'orders':
        return <OrdersPage onNavigate={navigateTo} />;
      case 'order-detail':
        return <OrderDetailPage orderId={subParams || ''} onBack={() => navigateTo('orders')} />;
      case 'repairs':
        return <RepairsPage onNavigate={navigateTo} />;
      case 'subscription':
        return <SubscriptionPage />;
      case 'gold-sale':
        return <GoldSalePage />;
      case 'old-gold':
        return <OldGoldPage onNavigate={navigateTo} />;
      case 'profile':
        return <ProfilePage />;
      case 'segment':
        return <SegmentDetailPage segmentType={subParams || ''} onBack={() => navigateTo('dashboard')} />;
      default:
        return <DashboardPage onNavigate={navigateTo} />;
    }
  };

  const getTrialBanner = () => {
    if (!subscription) return null;
    // Paid plans (BASIC/PRO/ENTERPRISE) that are still on TRIAL status
    if (subscription.status === 'TRIAL' && subscription.trialEnd && subscription.plan !== 'FREE') {
      const daysLeft = Math.ceil((new Date(subscription.trialEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
      if (daysLeft <= 0) return null;
      return (
        <div className={`${daysLeft <= 3 ? 'bg-red-50 border-red-200' : 'bg-blue-50 border-blue-200'} border-b px-4 py-2 text-center`}>
          <p className={`text-sm font-medium ${daysLeft <= 3 ? 'text-red-700' : 'text-blue-700'}`}>
            <FaExclamationTriangle className='inline mr-1' />
            Free trial ends in {daysLeft} day{daysLeft > 1 ? 's' : ''}.{' '}
            <button
              onClick={() => navigateTo('subscription')}
              className='underline font-semibold hover:no-underline'
            >
              Subscribe now
            </button>
          </p>
        </div>
      );
    }
    if (subscription.status === 'EXPIRED') {
      return (
        <div className='bg-red-50 border-red-200 border-b px-4 py-2 text-center'>
          <p className='text-sm font-medium text-red-700'>
            <FaExclamationTriangle className='inline mr-1' />
            Your subscription has expired.{' '}
            <button
              onClick={() => navigateTo('subscription')}
              className='underline font-semibold hover:no-underline'
            >
              Renew now
            </button>
          </p>
        </div>
      );
    }
    return null;
  };

  // Locked account -> payment/renewal screen only
  if (planLocked) {
    return (
      <div className="min-h-screen bg-slate-50 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 text-center bg-red-50 border border-red-200 rounded-2xl p-6 animate-fade-in">
            <FaLock className="text-red-500 text-3xl mx-auto mb-3" />
            <h1 className="text-2xl font-bold text-slate-900">Your plan has ended</h1>
            <p className="text-slate-600 mt-1">
              Renew your yearly plan to get your store live again. All your data is safe.
            </p>
          </div>
          <SubscriptionPage />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen md:grid md:grid-cols-[280px_1fr]">
      {/* Desktop sidebar */}
      <aside className="hidden md:block sticky top-0 h-screen">
        <Navbar currentPage={currentPage} onPageChange={navigateTo} />
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200/70 px-4 py-3 flex items-center gap-3 safe-top">
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center shrink-0"
          aria-label="Open menu"
        >
          <FaBars />
        </button>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <FaGem className="text-amber-500 text-lg shrink-0" />
          <span className="font-bold text-slate-900 truncate">ApniCRM</span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[11px] font-semibold text-slate-500">
            {subscription?.plan || ''}
          </span>
          <span
            className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
              subscription?.status === 'ACTIVE'
                ? 'bg-emerald-100 text-emerald-700'
                : subscription?.status === 'TRIAL'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-red-100 text-red-700'
            }`}
          >
            {subscription?.status || ''}
          </span>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm animate-fade-in"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 w-[280px] max-w-[85vw] shadow-2xl animate-slide-in-left">
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center"
              aria-label="Close menu"
            >
              <FaTimes />
            </button>
            <Navbar
              currentPage={currentPage}
              onPageChange={navigateTo}
              onClose={() => setMobileMenuOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Right column: banner + page content (must be ONE grid child) */}
      <div className="min-w-0 flex flex-col">
        {/* Demo mode banner */}
        {subscription?.plan === 'FREE' && (
          <div className="bg-gradient-to-r from-amber-400 to-amber-600 px-4 py-2.5 text-center">
            <p className="text-sm font-semibold text-white">
              🎯 Demo Preview{subscription.trialEnd
                ? ` — ${Math.max(0, Math.ceil((new Date(subscription.trialEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))} days free trial`
                : ''}{' '}
              ·{' '}
              <button
                onClick={() => navigateTo('subscription')}
                className="underline underline-offset-2 font-bold hover:text-slate-900 transition-colors"
              >
                Choose a plan to go live →
              </button>
            </p>
          </div>
        )}

        {/* Main content */}
        <div className="min-w-0">
          {getTrialBanner()}
          <main className="p-4 md:p-8 max-w-7xl mx-auto">{renderPage()}</main>
        </div>

        {/* Welcome / demo preview popup */}
        {showWelcome && (
          <WelcomeModal
            onClose={closeWelcome}
            onBuyPlans={() => {
              closeWelcome();
              navigateTo('subscription');
            }}
          />
        )}
      </div>
    </div>
  );
};

export default MainApp;